import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../ui';
import { newerBuildServed } from './newBuild';

/**
 * How often an open console asks whether it has been replaced. It also asks whenever the
 * tab is looked at again and whenever the network returns, which is when a person is
 * about to act on what the tab shows.
 */
const CHECK_EVERY_MS = 5 * 60_000;

/** How long Later hides the notice before it asks again. */
const ASK_AGAIN_AFTER_MS = 2 * 60_000;

/**
 * "A new version of the console is ready", with Reload beside it, once a deploy has
 * replaced the build this tab is running.
 *
 * Offered, never done for them: a reload in the middle of a call or a half-typed order
 * loses the half. Reloading keeps the session, since the refresh token is kept for exactly
 * that, and the sentence says so — otherwise the person the notice is for waits for the
 * end of their shift. Later puts it off for two minutes, not for good: it comes back until
 * they reload, because a console left on the previous build runs it for the rest of the
 * shift. A view that finds its files gone before then reloads by itself, in `main.tsx`.
 *
 * Mounted once, above every whole screen, so it appears on sign-in and the platform screen
 * as well as in the console. Bottom centre, the shape of the settings pages' save bar,
 * because the top bar is full at tablet width and the map's controls own both top corners.
 */
export function NewBuildNotice() {
  const { t } = useTranslation();
  const [newer, setNewer] = useState(false);
  const [later, setLater] = useState(false);

  useEffect(() => {
    if (!later) return;

    const timer = globalThis.setTimeout(() => setLater(false), ASK_AGAIN_AFTER_MS);
    return () => globalThis.clearTimeout(timer);
  }, [later]);

  useEffect(() => {
    const running: string | undefined = import.meta.env.CONSOLE_BUILD;
    if (newer || running === undefined) return;

    let live = true;

    const check = () => {
      void newerBuildServed(running).then((found) => {
        if (found && live) setNewer(true);
      });
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    const timer = globalThis.setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', check);

    return () => {
      live = false;
      globalThis.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', check);
    };
  }, [newer]);

  if (!newer || later) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 w-max max-w-[calc(100vw-2rem)] flex items-center gap-4 px-5 py-2.5 bg-surface border border-rule rounded-card shadow-float"
    >
      <span className="text-secondary text-ink-2">{t('app.newBuild')}</span>
      <div className="flex items-center gap-2">
        <Button variant="quiet" onClick={() => setLater(true)}>
          {t('app.newBuildLater')}
        </Button>
        <Button variant="primary" onClick={() => window.location.reload()}>
          {t('app.reload')}
        </Button>
      </div>
    </div>
  );
}
